import * as zod from 'zod';

import { TCPOffsetOption } from '@sb/motion-planning';
import { ActionRequiredError, Step } from '@sb/remote-control/types';
import type { Space } from '@sb/routine-runner';
import { Expression } from '@sb/routine-runner';

/**
 * Types matching the routine runner schema for "Update Visualization" statements.
 */
export namespace UpdateVisualizationStep {
  export const name = 'Update visualization';
  export const description = 'Show or hide space items in the visualizer';
  export const isDecorator = false;
  export const librarySection = Step.LibrarySection.Control;
  export const librarySort = '4';
  export const argumentKind = 'UpdateVisualization';

  /** Arguments to be passed to the routine runner */
  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    /**
     * The target kind to use for visualization updates
     */
    targetKind: zod.enum(['space', 'workpiece']),
    /**
     * The space item ID to target for visualization updates
     */
    targetSpaceItemId: zod.string(),
    /**
     * The action to perform on the visualization
     */
    action: zod.enum(['show', 'hide']),
    /**
     * Palletizing-specific configuration
     */
    palletizing: zod
      .object({
        /**
         * The scope of the action - which boxes to affect
         */
        scope: zod.enum(['allBoxes', 'allBoxesInLayer', 'specificBox']),
        /**
         * Whether to show the visualization as opaque (true) or transparent (false)
         */
        opacity: zod.boolean(),
        /**
         * Whether to show labels (true) or hide labels (false)
         */
        labels: zod.boolean(),
        /**
         * Layer index expression for specific layer targeting (optional)
         */
        layerIndex: Expression.optional(),
        /**
         * Box index expression for specific box targeting (optional)
         */
        boxIndex: Expression.optional(),
        /**
         * Box type ID for visualizing a tooltip box (optional)
         */
        boxTypeID: zod.string().optional(),
        /**
         * Pallet base ID for targeting specific pallet base (optional)
         */
        palletBaseID: zod.string().optional(),
      })
      .optional(),
    /**
     * Position list-specific configuration
     */
    positionList: zod
      .object({
        /**
         * The position scope for position lists - which positions to affect
         */
        positionScope: zod.enum(['allPositions', 'specificPosition']),
        /**
         * Position index expression for specific position targeting (optional)
         */
        positionIndex: Expression.optional(),
      })
      .optional(),
    /**
     * Workpiece-specific configuration
     */
    workpiece: zod
      .object({
        /**
         * The workpiece visualization type - tooltip or pickup position
         */
        visualizationType: zod.enum(['tooltip', 'pickup']),
        /**
         * The specific tooltip to use when visualizationType is 'tooltip'
         */
        tooltipOption: TCPOffsetOption.optional(),
      })
      .optional(),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    return {
      ...stepData,
      stepKind: 'UpdateVisualization',
      args: {
        targetKind: args.targetKind,
        targetSpaceItemId: args.targetSpaceItemId,
        action: args.action,
        ...(args.palletizing && { palletizing: args.palletizing }),
        ...(args.positionList && { positionList: args.positionList }),
        ...(args.workpiece && { workpiece: args.workpiece }),
      },
    };
  };

  export const validator: Step.Validator = ({
    stepConfiguration,
    routine,
    globalSpace,
  }) => {
    const args = (stepConfiguration?.args || {}) as Arguments;

    if (args?.argumentKind !== argumentKind) {
      return;
    }

    // Check if target space item is selected
    if (!args.targetSpaceItemId) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Target space item required',
        fieldId: 'targetSpaceItemId',
      });
    }

    // Validate that the target space item exists
    let routineSpace: Space.Item[] = [];

    if (routine?.space) {
      if (Array.isArray(routine.space)) {
        routineSpace = routine.space;
      } else {
        routineSpace = [routine.space];
      }
    }

    const allSpaceItems = [...globalSpace, ...routineSpace];

    const targetItem = allSpaceItems.find(
      (item) => item.id === args.targetSpaceItemId,
    );

    if (!targetItem) {
      throw new ActionRequiredError({
        kind: 'invalidConfiguration',
        message: 'Target space item not found',
        fieldId: 'targetSpaceItemId',
      });
    }

    // Check palletizing-specific validation
    if (args.palletizing && args.targetKind === 'space') {
      // Check if layerIndex is required and provided
      if (
        (args.palletizing.scope === 'allBoxesInLayer' ||
          args.palletizing.scope === 'specificBox') &&
        !args.palletizing.layerIndex
      ) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Layer index required',
          fieldId: 'palletizing.layerIndex',
        });
      }

      // Check if boxIndex is required and provided
      if (
        args.palletizing.scope === 'specificBox' &&
        !args.palletizing.boxIndex
      ) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Box index required',
          fieldId: 'palletizing.boxIndex',
        });
      }
    }

    // Check position list-specific validation
    if (args.positionList) {
      // Check if positionIndex is required and provided for position lists
      if (
        targetItem &&
        (targetItem.kind === 'freeformPositionList' ||
          targetItem.kind === 'gridPositionList') &&
        args.positionList.positionScope === 'specificPosition' &&
        !args.positionList.positionIndex
      ) {
        throw new ActionRequiredError({
          kind: 'invalidConfiguration',
          message: 'Position index required',
          fieldId: 'positionList.positionIndex',
        });
      }
    }
  };

  export const getStepDescription: Step.GetStepDescription = ({
    stepConfiguration: { args },
    routine,
    globalSpace,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      return null;
    }

    const allSpaceItems = [...(globalSpace ?? []), ...(routine?.space ?? [])];

    const targetItem = allSpaceItems.find(
      (item) => item.id === args.targetSpaceItemId,
    );

    if (!targetItem) {
      return null;
    }

    const actionText = args.action === 'show' ? 'Show' : 'Hide';
    const targetName = targetItem.name;

    // Handle space item targeting
    if (args.targetKind === 'space') {
      if (targetItem.kind === 'palletBoxes' && args.palletizing) {
        const { scope, opacity, labels } = args.palletizing;

        let scopeText = '';

        if (scope === 'allBoxes') {
          scopeText = 'all boxes';
        } else if (scope === 'allBoxesInLayer') {
          scopeText = 'all boxes in layer';
        } else if (scope === 'specificBox') {
          scopeText = 'specific box';
        }

        const details = [];

        if (args.action === 'show') {
          if (opacity !== undefined) {
            details.push(opacity ? 'as opaque' : 'as transparent');
          }

          if (labels !== undefined) {
            const labelText =
              scope === 'specificBox' ? 'with label' : 'with labels';

            details.push(
              labels ? labelText : labelText.replace('with', 'without'),
            );
          }
        }

        const detailsText = details.length > 0 ? ` ${details.join(', ')}` : '';

        return `${actionText} ${scopeText} in ${targetName}${detailsText}`;
      }

      if (
        (targetItem.kind === 'freeformPositionList' ||
          targetItem.kind === 'gridPositionList') &&
        args.positionList
      ) {
        const { positionScope } = args.positionList;

        const positionText =
          positionScope === 'allPositions'
            ? 'all positions'
            : 'specific position';

        return `${actionText} ${positionText} in ${targetName}`;
      }

      return `${actionText} ${targetName}`;
    }

    // Handle workpiece targeting
    if (args.targetKind === 'workpiece' && args.palletizing?.boxTypeID) {
      const boxType =
        targetItem.kind === 'palletBoxes'
          ? targetItem.boxTypes.find(
              (bt) => bt.id === args.palletizing?.boxTypeID,
            )
          : null;

      if (boxType) {
        const visualizationType = args.workpiece?.visualizationType;

        const typeText =
          visualizationType === 'tooltip' ? 'tooltip' : 'pickup position';

        return `${actionText} ${boxType.name} at ${typeText} in ${targetName}`;
      }
    }

    return `${actionText} ${targetName}`;
  };
}

UpdateVisualizationStep satisfies Step.StepKindInfo;
